import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import FilterComponent from "../../ReusableFIlterComponent/FilterComponent";
import store from "../../../store";
import {
  setCommissionPlansFilter,
  setCommissionPlansPage,
  setCommissionPlansSearchQuery,
} from "../../../features/dealsPromotionsSlice";

export default function CommissionPlansFilterPanel() {
  const [, setSearchParams] = useSearchParams();

  const { filters } = useSelector(
    (state: any) => state.dealsPromotions.commissionPlans,
  );
  const dealsPortfolioPlans = useSelector(
    (state: any) => state.dealsPromotions.dealsPortfolioPlans,
  );

  const [searchBrandId, setSearchBrandId] = useState("");
  const [searchWebsite, setSearchWebsite] = useState("");
  const [searchPlanName, setSearchPlanName] = useState("");
  const [searchPlanTerm, setSearchPlanTerm] = useState("");

  const searchFilteredData = dealsPortfolioPlans ?? [];

  const statusStaticData = [
    { status: "Complete" },
    { status: "Partial" },
  ];

  // Hydrate Redux from FilterComponent's ?filters= URL param on mount,
  // or apply default Partial filter when no prior session state exists in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filtersString = params.get("filters");
    const hasExistingState =
      filtersString || params.get("search") || (params.get("page") && params.get("page") !== "0");

    if (filtersString) {
      filtersString.split("&").forEach((part) => {
        const [key, value] = part.split("=");
        if (key && value) {
          const values = value.split(",").map(decodeURIComponent);
          store.dispatch(
            setCommissionPlansFilter({
              key: key as any,
              value: values,
            }),
          );
        }
      });
    } else if (!hasExistingState) {
      // Truly fresh entry — apply default Partial filter to both Redux and URL
      store.dispatch(setCommissionPlansFilter({ key: "status", value: ["Partial"] }));
      setSearchParams(
        (prev) => {
          prev.set("filters", "status=Partial");
          return prev;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchFilter = (key: string, value: string[]) => {
    const current = filters[key as keyof typeof filters];
    if (JSON.stringify(current) === JSON.stringify(value)) return;
    store.dispatch(setCommissionPlansFilter({ key: key as any, value }));
  };

  const filterConfig = [
    {
      key: "brand_id",
      name: "Brand ID",
      label: "Brand ID",
      searchTerm: searchBrandId,
      showSearch: true,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchBrandId(e.target.value),
      selectedOption: filters.brand_id,
      filterName: "brand_id",
      data: searchFilteredData,
      onSelectionChange: (val: string[]) => dispatchFilter("brand_id", val),
      count: filters.brand_id.length,
      colorSelected: filters.brand_id.length > 0 ? "#9C27B0" : "#000000DE",
      multiSelect: true,
    },
    {
      key: "website",
      name: "Website",
      label: "Website",
      searchTerm: searchWebsite,
      showSearch: true,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchWebsite(e.target.value),
      selectedOption: filters.website,
      filterName: "website",
      data: searchFilteredData,
      onSelectionChange: (val: string[]) => dispatchFilter("website", val),
      count: filters.website.length,
      colorSelected: filters.website.length > 0 ? "#9C27B0" : "#000000DE",
      multiSelect: true,
    },
    {
      key: "status",
      name: "Status",
      label: "Status",
      showSearch: false,
      selectedOption: filters.status,
      filterName: "status",
      data: statusStaticData,
      onSelectionChange: (val: string[]) => dispatchFilter("status", val),
      count: filters.status.length,
      colorSelected: filters.status.length > 0 ? "#9C27B0" : "#000000DE",
      multiSelect: false,
    },
    {
      key: "plan_name",
      name: "Plan Name",
      label: "Plan Name",
      searchTerm: searchPlanName,
      showSearch: true,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchPlanName(e.target.value),
      selectedOption: filters.plan_name,
      filterName: "plan_name",
      data: searchFilteredData,
      onSelectionChange: (val: string[]) => dispatchFilter("plan_name", val),
      count: filters.plan_name.length,
      colorSelected: filters.plan_name.length > 0 ? "#9C27B0" : "#000000DE",
      multiSelect: true,
      isLongText: true,
    },
    {
      key: "plan_term",
      name: "Plan Term",
      label: "Plan Term",
      searchTerm: searchPlanTerm,
      showSearch: true,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchPlanTerm(e.target.value),
      selectedOption: filters.plan_term,
      filterName: "plan_term",
      data: searchFilteredData,
      onSelectionChange: (val: string[]) => dispatchFilter("plan_term", val),
      count: filters.plan_term.length,
      colorSelected: filters.plan_term.length > 0 ? "#9C27B0" : "#000000DE",
      multiSelect: true,
    },
  ];

  const clearFilters = () => {
    setSearchBrandId("");
    setSearchWebsite("");
    setSearchPlanName("");
    setSearchPlanTerm("");
    (["brand_id", "website", "status", "plan_name", "plan_term"] as const).forEach(
      (key) => store.dispatch(setCommissionPlansFilter({ key, value: [] })),
    );
    store.dispatch(setCommissionPlansSearchQuery(""));
    store.dispatch(setCommissionPlansPage(0));
    // Clear FilterComponent's ?filters= param, ?search=, and reset ?page=
    setSearchParams(
      (prev) => {
        prev.delete("filters");
        prev.delete("search");
        prev.set("page", "0");
        return prev;
      },
      { replace: true },
    );
  };

  return (
    <Box>
      <FilterComponent
        filterConfig={filterConfig}
        clearFilters={clearFilters}
        filterButtonTrackerId="792"
        clearFilterTrackerId="793"
      />
    </Box>
  );
}
